// Digs one level deeper than "window size" — into the actual CUT PARTS the
// shop produces for every window: frame lengths, sash heights, glazbead,
// reinforcement, glass panes, and screens. This is the data that answers
// "what should we pre-cut and have ready on the shelf," because it's the
// exact stock-length breakdown, not just the overall window dimension.
//
// Source: `job.calculatedWindows[]`, the output of calculateWindow() (see
// src/calculations/*.js) that gets saved to Firestore once a job's cut list
// is generated (CutListPage.jsx). Every window type (DH/HS/CSMT/PW/BSMT/
// 3HS/BM) returns the same shape: { frame[], sash[], glazbead[],
// reinforcement[], misc[], glass[], flexScreen, stdScreen }, each linear
// part shaped { label, qty, length, part } and each glass/screen item
// shaped with { width, height, qty }.
//
// Not every sold job has run through the cut-list step yet, so this module
// is explicit about coverage rather than silently under-counting.

const ORDERED_STATUSES = new Set(['sold', 'in_production', 'ready', 'installed']);

const CUT_TYPE_LABELS = {
  DH: 'Double Hung', HS: 'Horizontal Slider', CSMT: 'Casement', PW: 'Picture Window',
  BSMT: 'Basement Hopper', '3HS': '3-Lite Slider', '3LHS': '3-Lite Slider', BM: 'Brickmould',
};

const SECTION_LABELS = {
  frame: 'Frame', sash: 'Sash', glazbead: 'Glazbead', reinforcement: 'Reinforcement', misc: 'Misc/Trim',
};

function cutTypeLabel(code) {
  return CUT_TYPE_LABELS[code] || code || 'Unknown';
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
}

function getJobDate(job) {
  return toDate(job.soldDate) || toDate(job.createdAt);
}

function roundInches(n) {
  return Math.round(n * 10000) / 10000;
}

const LINEAR_SECTIONS = ['frame', 'sash', 'glazbead', 'reinforcement', 'misc'];

// Flattens every cut part across sold-or-further jobs whose cut list has
// actually been generated. Returns separate row lists for linear parts
// (single length), glass panes (width x height), and screens (width x
// height), plus a coverage count of how many jobs contributed.
export function flattenCutParts(cutlistJobs, { from, to } = {}) {
  const linearRows = [];
  const glassRows = [];
  const screenRows = [];
  let jobsWithCalc = 0;
  let jobsConsidered = 0;

  cutlistJobs.forEach(job => {
    if (!ORDERED_STATUSES.has(job.status)) return;

    const orderDate = getJobDate(job);
    if (from && (!orderDate || orderDate < from)) return;
    if (to && orderDate && orderDate > to) return;

    jobsConsidered += 1;
    const calc = job.calculatedWindows;
    if (!Array.isArray(calc) || calc.length === 0) return;
    jobsWithCalc += 1;

    calc.forEach(cw => {
      const type = cw.type;
      const typeLabel = cutTypeLabel(type);

      LINEAR_SECTIONS.forEach(section => {
        (cw[section] || []).forEach(part => {
          const length = Number(part.length);
          if (!Number.isFinite(length) || length <= 0) return;
          linearRows.push({
            jobId: job.id, type, typeLabel, color: cw.color || 'Unspecified',
            section, sectionLabel: SECTION_LABELS[section],
            partLabel: part.label || 'Unlabeled', length: roundInches(length),
            partNumber: part.part || '',
            qty: Number(part.qty) > 0 ? Number(part.qty) : 0,
          });
        });
      });

      (cw.glass || []).forEach(g => {
        const width = Number(g.width);
        const height = Number(g.height);
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;
        glassRows.push({
          jobId: job.id, type, typeLabel, color: cw.color || 'Unspecified',
          paneLabel: g.label || 'Glass', width: roundInches(width), height: roundInches(height),
          qty: Number(g.qty) > 0 ? Number(g.qty) : 0,
        });
      });

      if (cw.flexScreen) {
        const width = Number(cw.flexScreen.width);
        const height = Number(cw.flexScreen.height);
        if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
          screenRows.push({
            jobId: job.id, type, typeLabel, color: cw.color || 'Unspecified',
            screenLabel: 'FlexScreen', width: roundInches(width), height: roundInches(height),
            qty: Number(cw.flexScreen.qty) > 0 ? Number(cw.flexScreen.qty) : 0,
          });
        }
      }
      if (cw.stdScreen) {
        const width = Number(cw.stdScreen.width);
        const height = Number(cw.stdScreen.height);
        const qty = (Number(cw.stdScreen.withPullQty) || 0) + (Number(cw.stdScreen.noPullQty) || 0);
        if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 && qty > 0) {
          screenRows.push({
            jobId: job.id, type, typeLabel, color: cw.color || 'Unspecified',
            screenLabel: 'Standard Screen', width: roundInches(width), height: roundInches(height), qty,
          });
        }
      }
    });
  });

  return {
    linearRows, glassRows, screenRows,
    coverage: { jobsWithCalc, jobsWithoutCalc: jobsConsidered - jobsWithCalc, totalJobs: jobsConsidered },
  };
}

function bumpTally(map, key, amount) {
  map.set(key, (map.get(key) || 0) + amount);
}

function finalizeTally(map) {
  const list = Array.from(map.entries())
    .map(([value, qty]) => ({ value, qty }))
    .sort((a, b) => b.qty - a.qty);
  const label = list.map(e => (list.length > 1 ? `${e.value} ×${e.qty}` : e.value)).join(', ');
  return { list, label };
}

function aggregateRows(rows, { key, initExtra, updateExtra }) {
  const map = new Map();
  rows.forEach(r => {
    const k = key(r);
    if (!map.has(k)) {
      map.set(k, { key: k, timesOrdered: 0, totalQty: 0, jobIds: new Set(), ...(initExtra ? initExtra(r) : {}) });
    }
    const entry = map.get(k);
    entry.timesOrdered += 1;
    entry.totalQty += r.qty;
    entry.jobIds.add(r.jobId);
    if (updateExtra) updateExtra(entry, r);
  });
  const totalQtyAll = rows.reduce((sum, r) => sum + r.qty, 0);
  return Array.from(map.values())
    .map(e => ({ ...e, jobCount: e.jobIds.size, pctOfTotal: totalQtyAll > 0 ? (e.totalQty / totalQtyAll) * 100 : 0 }))
    .sort((a, b) => b.totalQty - a.totalQty);
}

// Ranks EXACT (type, section, part label, length, COLOR) combinations by
// pieces needed — this is the pre-cut shopping list: "we need N pieces of
// this exact stock length, in this exact color, more often than any other."
// Color is part of the key, not a tally — a White 5150 and a Clay 5150 are
// different stock, never the same pre-cut shelf item. Part # is still
// tallied defensively (fin-type can occasionally vary the stock code for
// an otherwise identical type/section/label/length/color combo).
export function summarizeLinearParts(rows) {
  return aggregateRows(rows, {
    key: r => `${r.type}|${r.section}|${r.partLabel}|${r.length}|${r.color}`,
    initExtra: r => ({
      type: r.type, typeLabel: r.typeLabel, section: r.section, sectionLabel: r.sectionLabel,
      partLabel: r.partLabel, length: r.length, color: r.color, partNumberTally: new Map(),
    }),
    updateExtra: (entry, r) => { if (r.partNumber) bumpTally(entry.partNumberTally, r.partNumber, r.qty); },
  }).map(e => {
    const partNumbers = finalizeTally(e.partNumberTally);
    return { ...e, partNumbers: partNumbers.label, partNumberList: partNumbers.list };
  });
}

// Ranks EXACT (type, pane, width x height, COLOR) glass sizes by units needed.
export function summarizeGlassSizes(rows) {
  return aggregateRows(rows, {
    key: r => `${r.type}|${r.paneLabel}|${r.width}|${r.height}|${r.color}`,
    initExtra: r => ({ type: r.type, typeLabel: r.typeLabel, paneLabel: r.paneLabel, width: r.width, height: r.height, color: r.color }),
  }).map(e => ({ ...e, size: `${e.width}" x ${e.height}"` }));
}

// Ranks EXACT (type, screen kind, width x height, COLOR) screen sizes by units needed.
export function summarizeScreenSizes(rows) {
  return aggregateRows(rows, {
    key: r => `${r.type}|${r.screenLabel}|${r.width}|${r.height}|${r.color}`,
    initExtra: r => ({ type: r.type, typeLabel: r.typeLabel, screenLabel: r.screenLabel, width: r.width, height: r.height, color: r.color }),
  }).map(e => ({ ...e, size: `${e.width}" x ${e.height}"` }));
}
